/**
 * "When you pay" — `docs/proposal.md` §6.5, board 13 cells 5–6.
 *
 * A timeline: when, a dot on a rail, what it is for, how much. It is the
 * single most asked question about any quote, and leaving it to a phone call
 * is how a deposit becomes a surprise.
 *
 * **Twelve identical rows say nothing eleven of them did not.** A run of five
 * or more equal payments folds into one line — "12 monthly payments · $450.00
 * each · on the 1st of each month · $5,400.00 in all" — with **Show all 12
 * payments** opening the real dates. `foldPayments()` in `shared` is the one
 * rule, tested there, and it keeps **every** item: folding is a drawing and
 * never a removal. The invoices come from the billing plan, not from what this
 * draws, and the builder's Billing tab goes on listing all of them (§1.5).
 *
 * **A date here is a billing date, never a working one** (rule 14). The server
 * sends `on` only where the plan itself supplies the day, so an unfixed row
 * reads "Month 2" — a production date on a payment list is a promise nobody
 * made.
 *
 * **When and what are one pair of lines** (Aaron's review, item 2.4). Board
 * 13 drew the date column at 13.5 and 12px, the words beside it at 14.5 and
 * 12.5, and between them a series' marker as a 40px pill — so "Nov 1 / to
 * May 1" sat a few pixels off "7 monthly payments / $950.06 each", in a
 * different size, either side of a green lozenge that looked like a control.
 * Every row now sets its first line in `FIRST` and its second in `SECOND` on
 * all three sides, on one 20px and one 18px line box, so the baselines are
 * level by construction; and the rail is a hairline with an 8px dot centred
 * on the first line, whatever the row is.
 *
 * **Show all is the same rows** (item 2.6): one line per payment — the date
 * in the date column, a small dot on the rail, then the amount — rather than
 * a grid of chips, which at a phone's two columns truncated "Nov 1" to "N…"
 * and could not be read at all.
 */
import { useState } from 'react'
import { foldPayments, type PaymentRow, type ProposalView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollarsExact } from '../../lib/format.js'
import { GlyphChevronDown, GlyphChevronUp, GlyphInfo } from './glyphs.js'
import { TermsNote } from './terms-note.js'

export function PaymentsTimeline({ view, payments, phone }: {
  view: ProposalView
  payments: ProposalView['payments']
  phone: boolean
}) {
  const rows = foldPayments(payments, 5)
  const estimated = payments.some((row) => row.estimate)
  const caps = view.plans
    .filter((plan) => plan.cap !== null)
    .map((plan) => `${plan.name} will not exceed ${formatDollarsExact(plan.cap ?? 0)}`)

  return (
    <div>
      <div className="mb-[18px] flex items-center justify-between gap-3">
        <b className="text-[18px] tracking-[-0.01em] text-text">When you pay</b>
        <span className="tnum text-[13px] text-text-muted">
          {view.recurring.length > 0 && payments.length === 0
            ? 'Open-ended'
            : `${payments.length} ${payments.length === 1 ? 'payment' : 'payments'}`}
        </span>
      </div>

      <div className="flex flex-col">
        {rows.map((row, index) => (
          <TimelineRow
            key={key(row, index)}
            row={row}
            first={index === 0}
            last={index === rows.length - 1}
            phone={phone}
          />
        ))}
      </div>

      {/*
        * Open-ended work has no last payment, so it has no list and must not
        * be made to look like one. `view.recurring` is how the page already
        * knows a rental is on the quote (§4.4), and this is the sentence that
        * says what the customer is actually agreeing to.
        */}
      {view.recurring.length > 0 && (
        <Note>
          It ends when you tell us it does. The last invoice is for the days it
          was used.
        </Note>
      )}

      {(estimated || caps.length > 0) && (
        <Note>
          {estimated && 'Amounts marked “est.” are estimates — that work is invoiced '
            + 'from the hours actually worked and the materials actually used. '}
          {caps.join('. ')}{caps.length > 0 && '.'}
        </Note>
      )}
      <TermsNote terms={view.invoiceTerms} />
    </div>
  )
}

const key = (row: PaymentRow, index: number): string => (row.kind === 'series'
  ? `${row.seriesKey}:${index}`
  : `${row.payment.planId}:${row.payment.whenLabel}:${index}`)

/*
 * The two line boxes every column of a row shares. One constant each, so the
 * date, the words and the amount cannot drift apart again: the same size,
 * weight and line height on the left as on the right is what makes them read
 * as one pair of lines rather than three columns that nearly agree.
 */
const FIRST = 'text-[14.5px] font-semibold leading-5'
const SECOND = 'text-[12.5px] leading-[18px] text-text-faint'

/**
 * The date column and the gaps either side of the rail, the same on every row
 * and on every Show all line. 104px holds "After the work" on one line at the
 * words' own size; on a phone 64px holds "Month 12" and "to May 1", and the
 * room it gives back is what keeps "12 monthly payments" on one line beside
 * them — a title that wraps puts its second half beside the date's second
 * line, which is the mismatch this whole layout exists to remove.
 */
const whenWidth = (phone: boolean): string => (phone ? 'w-16' : 'w-[104px]')
const rowGap = (phone: boolean): string => (phone ? 'gap-2.5' : 'gap-3.5')

function TimelineRow({ row, first, last, phone }: {
  row: PaymentRow
  first: boolean
  last: boolean
  phone: boolean
}) {
  const [open, setOpen] = useState(false)
  /* "When you accept" is board 2's "Today", in the brand, with a filled dot. */
  const now = row.kind !== 'series' && row.payment.whenLabel === 'When you accept'
  const items = row.kind === 'series' && open ? row.items : []
  /*
   * Each Show all line names what it pays for (Aaron, step 25: "Apr 1 ·
   * April · $412.86") — but only where the names differ. A run whose every
   * row says "Monthly payment" (a plan with no date yet) would print the
   * folded row's own title twelve times.
   */
  const named = new Set(items.map((item) => item.label)).size > 1

  return (
    <>
      <div className={cn('flex', rowGap(phone))}>
        {/* When. Two lines on a series: the first date and "to" the last. */}
        <div className={cn('flex shrink-0 flex-col text-right', whenWidth(phone))}>
          {row.kind === 'series' ? (
            <>
              <span className={cn(FIRST, 'text-text')}>{row.firstLabel}</span>
              <span className={SECOND}>to {row.lastLabel}</span>
            </>
          ) : (
            <span className={cn(FIRST,
              /* The first thing due wears the brand: it is the one that is now. */
              now ? 'text-brand-tx' : 'text-text')}>
              {now ? 'Today' : row.payment.whenLabel}
            </span>
          )}
        </div>

        <Rail dot={now ? 'now' : 'due'} top={!first} bottom={!last || items.length > 0} />

        <div className={cn('min-w-0 flex-1', !last || items.length > 0 ? 'pb-[18px]' : 'pb-0')}>
          <div className="flex justify-between gap-3">
            <div className="flex min-w-0 flex-col">
              <span className={cn(FIRST, 'text-text')}>
                {row.kind === 'series' ? row.title : row.payment.label}
              </span>
              <span className={cn(SECOND, 'tnum')}>
                {row.kind === 'series'
                  ? [`${cents(row.each)} each`, row.cadence]
                    .filter(Boolean).join(' · ')
                  : now ? row.payment.note || 'when you accept' : row.payment.note}
              </span>
            </div>
            <div className="flex shrink-0 flex-col items-end">
              <span className={cn(FIRST, 'tnum text-text')}>
                {cents(row.kind === 'series' ? row.inAll : row.payment.amount)}
                {(row.kind === 'series' ? row.estimate : row.payment.estimate) && (
                  <span className="font-normal text-text-faint"> est.</span>
                )}
              </span>
              {row.kind === 'series' && <span className={SECOND}>in all</span>}
            </div>
          </div>

          {row.kind === 'series' && (
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              /* 44px of target under a 13.5px link: §13, every width. */
              className="-my-2 mt-0.5 inline-flex min-h-[44px] items-center gap-1 py-2
                         text-[13.5px] font-semibold text-brand-tx"
            >
              {open ? `Hide the ${row.count} payments` : `Show all ${row.count} payments`}
              {open ? <GlyphChevronUp size={15} /> : <GlyphChevronDown size={15} />}
            </button>
          )}
        </div>
      </div>

      {/*
        * Show all: every payment in the run as a line of the timeline itself,
        * so a reader follows one rail from the folded row down through each
        * date and on to whatever comes after it.
        */}
      {items.map((item, index) => (
        <div key={`${item.whenLabel}:${index}`} data-payment-line
          className={cn('flex', rowGap(phone))}>
          <span className={cn('shrink-0 text-right text-text', FIRST, whenWidth(phone))}>
            {item.whenLabel}
          </span>
          <Rail dot="line" top bottom={!last || index < items.length - 1} />
          {/*
            * Date, what it is for, then amount. The month is not the date
            * said twice: the invoice is dated when it is sent and the label
            * is the period it pays for, and a window opening mid-month bills
            * October on Nov 1.
            */}
          <span className={cn(FIRST, 'tnum min-w-0 flex-1 text-text',
            index < items.length - 1 ? 'pb-2' : last ? 'pb-0' : 'pb-[18px]')}>
            {named && <span className="font-normal text-text-muted">{item.label} · </span>}
            {cents(item.amount)}
            {item.estimate && <span className="font-normal text-text-faint"> est.</span>}
          </span>
        </div>
      ))}
    </>
  )
}

/**
 * The rail beside one row: a hairline above, a dot level with the first line,
 * a hairline below.
 *
 * The dot sits 6px down a 20px line box, which is its centre; the hairline
 * runs through the row's own padding to the next row's dot, so the rail is
 * one unbroken line however tall the words beside it wrap. **A dot, never a
 * pill**: the folded row used to draw a 40px lozenge, which is a shape the
 * page uses for a switch.
 */
function Rail({ dot, top, bottom }: {
  /** `now` filled in the brand, `due` an open ring, `line` a small Show all point. */
  dot: 'now' | 'due' | 'line'
  top: boolean
  bottom: boolean
}) {
  return (
    <div className="flex w-2 shrink-0 flex-col items-center" aria-hidden="true">
      <i className={cn('w-px shrink-0', dot === 'line' ? 'h-[7px]' : 'h-1.5', top && 'bg-line')} />
      <i className={cn('shrink-0 rounded-full',
        dot === 'now' && 'h-2 w-2 bg-brand',
        dot === 'due' && 'h-2 w-2 border-[1.6px] border-brand-ui bg-surface',
        dot === 'line' && 'h-1.5 w-1.5 bg-line-strong')} />
      <i className={cn('w-px flex-1', bottom && 'bg-line')} />
    </div>
  )
}

/**
 * A quiet block on the page background, for the one or two sentences a
 * payment list needs beside it. No icon: the proposal draws glyphs where they
 * carry meaning — a division's mark, a calendar card — and an `i` in a circle
 * beside a sentence on a document somebody signs is decoration.
 */
export function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 mt-5 flex gap-2.5 rounded-[12px] bg-bg px-3.5 py-3 text-[13px] leading-[1.5]
                  text-text-muted">
      <GlyphInfo size={16} className="mt-px shrink-0 text-text-faint" />
      <span>{children}</span>
    </p>
  )
}

/** The schedule's figures carry their cents, as boards 2 and 13 print them. */
const cents = (amount: number): string => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount)
